// #genai SharedStartupGate — bootstraps every protected route.
//
// Flow
// ─────
// 1. Capture existing user-auth state (email from current token) BEFORE any API calls.
// 2. Call validateResourcePath and requestPreAccessToken in parallel.
// 3. If EITHER fails OR the resource is NOT shared → legacy path:
//      render children as-is; existing Okta auth guard takes over.
// 4. If BOTH succeed AND resource IS shared:
//    a. User has NO session → store pre-access token
//                           → write GuestAccess context to sessionStorage
//                           → redirect to /signin?redirect=<current path>
//    b. User already has a session:
//       - Determine isEvUser by decoding the token's `iss` claim (Okta = EV user)
//       - Call checkResourceAccess; dispatch isEvUser + hasAccess to Redux
//       - EV user → always pass through regardless of hasAccess
//       - Non-EV user + hasAccess false → render AccessDenied
//
// State hydration across full-browser-navigation boundaries
// ──────────────────────────────────────────────────────────
// This gate runs on every page load, so any full navigation back to a resource URL
// (e.g. after Okta completes and redirects to /authorize/callback → resource URL)
// causes it to re-execute, re-call the APIs, and re-populate Redux state.
// That is intentional — no extra hydration needed for the Okta redirect path.
//
// For the gate → /signin redirect (flow 4a above), the JS runtime is torn down by
// window.location.assign(), so Redux state cannot survive. Instead, the context is
// serialised to sessionStorage under GUEST_CONTEXT_SESSION_KEY before navigating.
// ConfigureStore.js reads that key at store-creation time and passes it as
// createStore(reducer, preloadedState, ...) so Redux starts with the correct values
// before any component renders. The key is consumed once and then removed.
//
// On any unexpected error the gate always falls back to the legacy path so the
// normal Okta auth guard can handle the request — the guest flow never blocks the app.
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  isTokenValid, clearExpiredToken, getAuthToken, getUserEmailFromToken, isOktaToken, isSignInAsToken,
} from 'utils/app.utils';
import {
  validateResourcePathService,
  requestPreAccessTokenService,
  storePreAccessToken,
  processBootstrapResults,
  checkAccessWithFallback,
} from 'services/GuestAccess.service';
import { GUEST_CONTEXT_SESSION_KEY, SHARED_LOGIN_ID_KEY } from 'constants/AppConstants';
import { updateUserAccessAuditService } from 'services/App.service';
import {
  setGuestResource,
  setGuestFlowPhase,
  setGuestHasAccess,
  setGuestUserInfo,
} from 'features/guest-access/GuestAccess.actions';
import AccessDenied from 'features/guest-access/AccessDenied.component';
import {
  setSharedContext,
  setSharedAccess,
  setSharedPhase,
} from './SharedStartup.actions';
import { ACCESS_PHASES, GUEST_ROUTES } from '../../constants/GuestAccess';
import Loader from '../../commonComponents/Loader/Loader';

// ─── Inline styles ────────────────────────────────────────────────────────────

// ─── URL helpers ──────────────────────────────────────────────────────────────

/**
 * Returns the non-empty path segments for the current URL.
 * e.g. "/truedesign/1234568" → ["truedesign", "1234568"]
 *      "/start"              → ["start"]
 *      "/"                   → []
 */
const getPathSegments = () => (window.location?.pathname || '').split('/').filter(Boolean);

/**
 * Returns true only when the URL contains both an app-code AND a resource-id segment,
 * e.g. "/truedesign/1234568".  Paths like "/" or "/start" return false and the gate
 * skips all API calls, falling straight through to the normal Okta flow.
 */
const hasResourcePath = () => getPathSegments().length >= 2;

/**
 * Returns the first path segment as the application code.
 * e.g. "/truedesign/1234568" → "truedesign"
 */
const getAppCodeFromPath = () => getPathSegments()[0] || 'viewpoint';

/**
 * Builds the canonical resource path expected by the validation APIs.
 * Format: "<hostname>/<pathname-without-leading-slash>"
 * e.g. "apps.cmh.platform-dev.eagleview.com/truedesign/1234568"
 */
const buildResourcePath = () => {
  const { pathname } = window.location;
  const path = pathname.replace(/^\//, '');
  return `${path}`;
};

// ─── Gate phases ─────────────────────────────────────────────────────────────
// 'loading'      – APIs in-flight
// 'legacy'       – not a shared resource (or error) → render children for Okta flow
// 'ready'        – shared resource + authenticated user → render children
// 'redirecting'  – shared resource + no user token → going to /signin
// 'accessDenied' – non-EV guest with hasAccess:false → render AccessDenied #genai

// ─── Component ───────────────────────────────────────────────────────────────

const SharedStartupGate = ({ children }) => {
  const dispatch = useDispatch();
  const [phase, setPhase] = useState(ACCESS_PHASES.LOADING);

  useEffect(() => {
    let cancelled = false;

    const fallbackToLegacy = () => {
      // Any failure path: let the existing Okta auth guard handle things.
      dispatch(setGuestFlowPhase(ACCESS_PHASES.LEGACY));
      dispatch(setSharedPhase(ACCESS_PHASES.LEGACY));
      if (!cancelled) setPhase(ACCESS_PHASES.LEGACY);
    };

    const bootstrap = async () => {
      // eslint-disable-next-line no-console
      console.log('[SharedStartupGate] bootstrap start', {
        pathname: window.location?.pathname,
        hasResourcePath: hasResourcePath(),
      });
      // Only run the guest flow for deep resource URLs (/{appCode}/{resourceId}).
      if (!hasResourcePath()) {
        // eslint-disable-next-line no-console
        console.log('[SharedStartupGate] bootstrap — no resource path, falling back to legacy');
        fallbackToLegacy();
        return;
      }

      // Read user token BEFORE storePreAccessToken() so we see the real session. #genai
      const rawEmail = getUserEmailFromToken();
      // Sign-in-as (impersonation) token: skip all guest APIs and use legacy Okta flow.
      if (rawEmail && isTokenValid() && isSignInAsToken()) {
        fallbackToLegacy();
        return;
      }
      if (rawEmail && !isTokenValid()) {
        clearExpiredToken();
      }
      const existingUserEmail = rawEmail && isTokenValid() ? rawEmail : '';

      const appCode = getAppCodeFromPath();
      const resourcePath = buildResourcePath();

      try {
        // ── Step 1: validate resource + request pre-access token in parallel
        const [validationResult, preAccessResult] = await Promise.all([
          validateResourcePathService(appCode, resourcePath),
          requestPreAccessTokenService(appCode, resourcePath),
        ]);

        if (cancelled) return;

        const bootstrapPayload = processBootstrapResults(validationResult, preAccessResult, appCode, resourcePath);
        const {
          isResourceShared, isPreAccessTokenOk, preAccessToken,
          resourceShareId, resourceId, appCode: resolvedAppCode,
        } = bootstrapPayload;

        // ── Step 2: persist results in Redux (common GuestAccess state) ──────
        dispatch(setGuestResource(bootstrapPayload));
        dispatch(setSharedContext({
          shared: isResourceShared,
          appCode: resolvedAppCode,
          resourcePath,
          resourceShareId,
        }));

        // ── Step 3: route to the correct flow ───────────────────────────────
        if (!isResourceShared || !isPreAccessTokenOk) {
          // When the resource is no longer shared and the current user holds a
          // non-Okta (guest) token, the legacy Okta auth guard cannot handle
          // them. Explicitly deny access so the guest sees AccessDenied instead
          // of being silently dropped into the Okta flow. #genai
          const currentToken = getAuthToken();
          if (!isResourceShared && existingUserEmail && !isOktaToken(currentToken)) {
            // eslint-disable-next-line no-console
            console.log('[SharedStartupGate] Access denied — resource no longer shared, guest token present', {
              isResourceShared,
              isPreAccessTokenOk,
              userEmail: existingUserEmail,
              appCode: resolvedAppCode,
              resourcePath,
            });
            dispatch(setGuestHasAccess({ hasAccess: false, resourceAccessAuditId: null }));
            dispatch(setGuestFlowPhase(ACCESS_PHASES.ACCESS_DENIED));
            if (!cancelled) setPhase(ACCESS_PHASES.ACCESS_DENIED);
            return;
          }
          // eslint-disable-next-line no-console
          console.log('[SharedStartupGate] Not a shared resource or pre-access token failed — falling back to legacy Okta flow', {
            isResourceShared,
            isPreAccessTokenOk,
            appCode: resolvedAppCode,
            resourcePath,
          });
          // Resource is not shared, or a backend error — use normal Okta flow.
          fallbackToLegacy();
          return;
        }

        if (existingUserEmail) {
          const currentToken = getAuthToken();
          // isEvUser derived from `iss` claim, not checkIsEvUser — works for returning users too. #genai
          const isEvUser = isOktaToken(currentToken);

          dispatch(setGuestUserInfo({ isEvUser, email: existingUserEmail }));
          dispatch(setSharedAccess({ isEVUser: isEvUser }));

          // Read-once loginId stored by SignInFlow before Okta redirect.
          let sharedLoginId = null;
          try {
            sharedLoginId = sessionStorage.getItem(SHARED_LOGIN_ID_KEY);
            if (sharedLoginId) sessionStorage.removeItem(SHARED_LOGIN_ID_KEY);
          } catch (_) { /* noop */ }

          const accessPromise = checkAccessWithFallback(resolvedAppCode, resourceId, currentToken);
          if (sharedLoginId) {
            updateUserAccessAuditService(sharedLoginId, { status: 'SUCCESS' }).catch(() => {});
          }

          const { hasAccess, resourceAccessAuditId } = await accessPromise;
          // eslint-disable-next-line no-console
          console.log('[SharedStartupGate] hasAccess check complete', {
            hasAccess,
            isEvUser,
            resourceAccessAuditId,
            appCode: resolvedAppCode,
            resourcePath,
            userEmail: existingUserEmail,
          });
          dispatch(setGuestHasAccess({ hasAccess, resourceAccessAuditId }));
          dispatch(setSharedAccess({ isEVUser: isEvUser, hasAccess, resourceAccessAuditId }));

          if (!isEvUser && !hasAccess) {
            // eslint-disable-next-line no-console
            console.log('[SharedStartupGate] Access denied — non-EV user with hasAccess:false', {
              userEmail: existingUserEmail,
              appCode: resolvedAppCode,
              resourcePath,
              resourceAccessAuditId,
            });
            dispatch(setGuestFlowPhase(ACCESS_PHASES.ACCESS_DENIED));
            if (!cancelled) setPhase(ACCESS_PHASES.ACCESS_DENIED);
            return;
          }

          // eslint-disable-next-line no-console
          console.log('[SharedStartupGate] Access granted — proceeding to app', {
            isEvUser,
            hasAccess,
            appCode: resolvedAppCode,
            resourcePath,
          });
          dispatch(setGuestFlowPhase(ACCESS_PHASES.VERIFIED));
          dispatch(setSharedPhase(ACCESS_PHASES.READY));
          if (!cancelled) setPhase(ACCESS_PHASES.READY);
          return;
        }

        // Unauthenticated user on a shared resource — start guest sign-in flow.
        // Write the pre-access token to its dedicated key (ev_pre_access_token).
        // This does NOT touch the main 'token' entry.
        // APIs 4, 5 and 6 will read it from there via withPreAccessAuth() headers.
        storePreAccessToken(preAccessToken);

        // Persist the GuestAccess context to sessionStorage BEFORE the redirect.
        // window.location.assign() is a full browser navigation that destroys the
        // Redux store. SignInFlow reads this on mount and re-hydrates Redux from it.
        try {
          sessionStorage.setItem(GUEST_CONTEXT_SESSION_KEY, JSON.stringify(bootstrapPayload));
        } catch (_) { /* noop — non-blocking */ }

        const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        dispatch(setGuestFlowPhase(ACCESS_PHASES.EMAIL_INPUT));
        dispatch(setSharedPhase(ACCESS_PHASES.REDIRECTING));
        if (!cancelled) {
          setPhase(ACCESS_PHASES.REDIRECTING);
          window.location.assign(`${GUEST_ROUTES.SIGNIN_REDIRECT_PREFIX}${encodeURIComponent(redirect)}`);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('[SharedStartupGate] bootstrap error — falling back to legacy', { error: err });
        fallbackToLegacy();
      }
    };

    bootstrap();
    return () => { cancelled = true; };
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (phase === ACCESS_PHASES.LEGACY || phase === ACCESS_PHASES.READY) {
    return <>{children}</>;
  }

  if (phase === ACCESS_PHASES.REDIRECTING) {
    return (
      <Loader />
    );
  }

  if (phase === ACCESS_PHASES.ACCESS_DENIED) {
    return (
      <AccessDenied
        userEmail={getUserEmailFromToken()}
        redirectUrl={window.location.pathname + window.location.search}
      />
    );
  }

  // 'loading' — APIs still in-flight
  return (
    <Loader />
  );
};

SharedStartupGate.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SharedStartupGate;
